// src/services/travelService.js

import {
  getTravel,
  addTravel,
  updateTravel,
  deleteTravel,
  eventBridgeService,
  EVENT_TYPES,
  removeDocumentReferenceBlocks,
  updateDocumentReferenceBlocks
} from '@trip-planner-monorepo/shared';
import { FaPlane, FaTrain, FaCar, FaShip, FaBus, FaPlus } from 'react-icons/fa';
import { TRAVEL_TYPES } from '../components/constants/travelTypes';

export const travelService = {
  // Fetch all travel items for a trip
  async fetchTravel(userId, tripId) {
    try {
      const travel = await getTravel(userId, tripId);
      return travel.sort((a, b) => {
        const dateA = a.details?.departureTime || a.details?.pickupTime;
        const dateB = b.details?.departureTime || b.details?.pickupTime;
        if (!dateA && !dateB) {
          // Fall back to creation order so undated items sort consistently
          return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
        }
        if (!dateA) return 1;
        if (!dateB) return -1;
        return new Date(dateA) - new Date(dateB);
      });
    } catch (error) {
      console.error('Error fetching travel:', error);
      throw error;
    }
  },

  // Create new travel item
  async createTravel(userId, tripId, travelData) {
    try {
      console.log('Creating travel with data:', travelData);
      const validation = this.validateTravelData(travelData);
      if (!validation.isValid) {
        throw new Error(validation.errors.join('\n'));
      }

      // Generate summary if not provided
      let summary = travelData.summary;
      if (!summary) {
        const typeConfig = TRAVEL_TYPES[travelData.type];
        if (typeConfig && typeConfig.createSummary && travelData.details) {
          summary = typeConfig.createSummary(travelData.details);
        } else {
          summary = `${travelData.type} travel`; // Fallback summary
        }
      }

      const newTravel = {
        type: travelData.type,
        details: travelData.details,
        status: travelData.status || 'Planned',
        summary: summary,
        cost: {
          amount: travelData.cost?.amount || 0,
          currency: travelData.cost?.currency || 'USD',
          paid: travelData.cost?.paid || false,
          dueDate: travelData.cost?.dueDate || null
        },
        notes: travelData.notes || '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      return await addTravel(userId, tripId, newTravel);
    } catch (error) {
      console.error('Error creating travel:', error);
      throw error;
    }
  },

  // Update travel item
  async updateTravel(userId, tripId, travelId, travelData) {
    try {
      const validation = this.validateTravelData(travelData);
      if (!validation.isValid) {
        throw new Error(validation.errors.join('\n'));
      }

      const updateData = {
        ...travelData,
        updatedAt: new Date().toISOString()
      };

      // Don't regenerate summary if already provided in travelData
      // This preserves the user-facing title across edits
      if (!updateData.summary && updateData.type) {
        const typeConfig = TRAVEL_TYPES[updateData.type];
        if (typeConfig && typeConfig.createSummary) {
          updateData.summary = typeConfig.createSummary(updateData.details);
        }
      }

      await updateTravel(userId, tripId, travelId, updateData);
      return this.fetchTravel(userId, tripId);
    } catch (error) {
      console.error('Error updating travel:', error);
      throw error;
    }
  },

  // Delete travel item
  async deleteTravel(userId, tripId, travelId) {
    try {
      // Fetch travel details before deletion for the event payload
      const allTravel = await getTravel(userId, tripId);
      const travelToDelete = allTravel.find(t => t.id === travelId);
      
      // Delete the travel item
      await deleteTravel(userId, tripId, travelId);
      
      // Publish TRAVEL_DELETED event for cleanup handlers
      if (travelToDelete) {
        console.log('🗑️ [TravelService] deleteTravel: Publishing TRAVEL_DELETED event', {
          travelId,
          travelType: travelToDelete.type,
          summary: travelToDelete.summary
        });
        
        eventBridgeService.publish(EVENT_TYPES.TRAVEL_DELETED, {
          userId,
          tripId,
          id: travelId,
          type: travelToDelete.type,
          summary: travelToDelete.summary,
          details: travelToDelete.details,
          timestamp: new Date().toISOString()
        });
      }
      
      return this.fetchTravel(userId, tripId);
    } catch (error) {
      console.error('Error deleting travel:', error);
      throw error;
    }
  },

  // Validate travel data
  validateTravelData(travelData) {
    const errors = [];

    // Validate basic structure
    if (!travelData.type) {
      errors.push('Travel type is required');
      return { isValid: false, errors };
    }

    // For flight type, check if we have enough info to create a title
    if (travelData.type === 'flight') {
      console.log('Validating flight data:', travelData.details); // Debug log

      // Check if details exists
      if (!travelData.details) {
        errors.push('Flight details are required');
        return { isValid: false, errors };
      }

      const hasTitleInfo = Boolean(
        travelData.details.airline?.trim() ||
        travelData.details.flightNumber?.trim() ||
        travelData.details.departureCity?.trim() ||
        travelData.details.arrivalCity?.trim() ||
        travelData.details.departureAirport?.trim() ||
        travelData.details.arrivalAirport?.trim()
      );

      console.log('Has title info:', hasTitleInfo); // Debug log

      if (!hasTitleInfo) {
        errors.push('Please provide at least one piece of flight information (airline, flight number, departure or arrival location)');
      }
    }
    // For train type, check if we have enough info to create a title - same approach as flight
    else if (travelData.type === 'train') {
      console.log('Validating train data:', travelData.details); // Debug log

      // Check if details exists
      if (!travelData.details) {
        errors.push('Train details are required');
        return { isValid: false, errors };
      }

      const hasTitleInfo = Boolean(
        travelData.details.operator?.trim() ||
        travelData.details.trainNumber?.trim() ||
        travelData.details.departureCity?.trim() ||
        travelData.details.arrivalCity?.trim() ||
        travelData.details.departureStation?.trim() ||
        travelData.details.arrivalStation?.trim()
      );

      console.log('Has title info:', hasTitleInfo); // Debug log

      if (!hasTitleInfo) {
        errors.push('Please provide at least one piece of train information (operator, train number, departure or arrival location)');
      }
    }
    // For bus type, check if we have enough info to create a title - similar approach as flight and train
    else if (travelData.type === 'bus') {
      console.log('Validating bus data:', travelData.details); // Debug log

      // Check if details exists
      if (!travelData.details) {
        errors.push('Bus details are required');
        return { isValid: false, errors };
      }

      const hasTitleInfo = Boolean(
        travelData.details.operator?.trim() ||
        travelData.details.serviceNumber?.trim() ||
        travelData.details.departureCity?.trim() ||
        travelData.details.arrivalCity?.trim()
      );

      console.log('Has title info:', hasTitleInfo); // Debug log

      if (!hasTitleInfo) {
        errors.push('Please provide at least one piece of bus information (operator, service number, departure or arrival city)');
      }
    } else {
      // For other travel types, use the original validation
      const typeConfig = TRAVEL_TYPES[travelData.type];
      if (!typeConfig) {
        errors.push('Invalid travel type');
        return { isValid: false, errors };
      }

      // Validate required fields
      Object.entries(typeConfig.fields).forEach(([fieldName, fieldConfig]) => {
        if (fieldConfig.required && !travelData.details?.[fieldName]) {
          errors.push(`${fieldConfig.label} is required`);
        }
      });
    }

    // Validate dates if present
    if (travelData.details) {
      const departureTime = travelData.details.departureTime;
      const arrivalTime = travelData.details.arrivalTime;

      if (departureTime && arrivalTime) {
        const departure = new Date(departureTime);
        const arrival = new Date(arrivalTime);
        if (arrival < departure) {
          errors.push('Arrival time cannot be before departure time');
        }
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  },

  // Get available travel types
  getTravelTypes() {
    return Object.entries(TRAVEL_TYPES).map(([key, config]) => ({
      id: key,
      ...config
    }));
  },

  // Get travel status options
  getTravelStatuses() {
    return [
      'Planned',
      'Booked',
      'Confirmed',
      'Completed',
      'Cancelled'
    ];
  },

  /**
   * Initializes the travel service
   * Subscribes to relevant events
   */
  init() {
    // Subscribe to document updated events
    eventBridgeService.subscribe(EVENT_TYPES.DOCUMENT_UPDATED, this.handleDocumentUpdated.bind(this));
    console.log(`TravelService subscribed to ${EVENT_TYPES.DOCUMENT_UPDATED} event.`);

    // Subscribe to document deleted events
    eventBridgeService.subscribe(EVENT_TYPES.DOCUMENT_DELETED, this.handleDocumentDeleted.bind(this));
    console.log(`TravelService subscribed to ${EVENT_TYPES.DOCUMENT_DELETED} event.`);

    console.log('Travel service initialized and all relevant event subscriptions are active.');
  },

  /**
   * Handles document updated events
   * Updates document reference blocks with new metadata
   * 
   * @param {Object} eventData - The document update event data
   */
  async handleDocumentUpdated(eventData) {
    try {
      const { userId, tripId, documentId, updateData } = eventData;
      const { linkRegistryConfigService } = await import(
        '@trip-planner-monorepo/shared/services/linkRegistryConfigService'
      );
      const config = await linkRegistryConfigService.getLinkRegistryConfig(userId, tripId);
      if (config.writeMode === 'registry_only') {
        return;
      }

      console.log('📄 [TravelService] handleDocumentUpdated: Processing document update', {
        userId,
        tripId,
        documentId,
        hasUpdateData: !!updateData
      });

      if (!userId || !tripId || !documentId) {
        console.warn('⚠️ [TravelService] handleDocumentUpdated: Missing required event data');
        return;
      }

      // Get all travel items for the trip
      const travelItems = await this.fetchTravel(userId, tripId);
      if (!travelItems || travelItems.length === 0) {
        console.log('✈️ [TravelService] handleDocumentUpdated: No travel items found for trip');
        return;
      }

      // Process each travel item to update document references
      for (const travelItem of travelItems) {
        if (!travelItem.contentBlocks || travelItem.contentBlocks.length === 0) continue;

        // Check if this travel item has document references for the updated document
        const hasDocumentReference = travelItem.contentBlocks.some(
          block => block.type === 'document_reference' && block.documentId === documentId
        );

        if (hasDocumentReference) {
          console.log('🔄 [TravelService] handleDocumentUpdated: Updating document references in travel item', {
            travelId: travelItem.id,
            documentId
          });

          // Create updated document object for the utility function
          const updatedDocument = {
            id: documentId,
            ...updateData
          };

          // Update document reference blocks
          const updatedContentBlocks = updateDocumentReferenceBlocks(travelItem.contentBlocks, updatedDocument);

          // Update the travel item with new content blocks
          await updateTravel(userId, tripId, travelItem.id, {
            contentBlocks: updatedContentBlocks
          });

          console.log('✅ [TravelService] handleDocumentUpdated: Successfully updated document references in travel item', {
            travelId: travelItem.id,
            documentId
          });
        }
      }
    } catch (error) {
      console.error('❌ [TravelService] handleDocumentUpdated: Error updating document references', {
        error: error.message,
        stack: error.stack
      });
    }
  },

  /**
   * Handles document deleted events
   * Removes document reference blocks from travel items
   * 
   * @param {Object} eventData - The document deletion event data
   */
  async handleDocumentDeleted(eventData) {
    try {
      const { userId, tripId, documentId } = eventData;
      const { linkRegistryConfigService } = await import(
        '@trip-planner-monorepo/shared/services/linkRegistryConfigService'
      );
      const config = await linkRegistryConfigService.getLinkRegistryConfig(userId, tripId);
      if (config.writeMode === 'registry_only') {
        return;
      }

      console.log('🗑️ [TravelService] handleDocumentDeleted: Processing document deletion', {
        userId,
        tripId,
        documentId
      });

      if (!userId || !tripId || !documentId) {
        console.warn('⚠️ [TravelService] handleDocumentDeleted: Missing required event data');
        return;
      }

      // Get all travel items for the trip
      const travelItems = await this.fetchTravel(userId, tripId);
      if (!travelItems || travelItems.length === 0) {
        console.log('✈️ [TravelService] handleDocumentDeleted: No travel items found for trip');
        return;
      }

      // Process each travel item to remove document references
      for (const travelItem of travelItems) {
        if (!travelItem.contentBlocks || travelItem.contentBlocks.length === 0) continue;

        // Check if this travel item has document references for the deleted document
        const hasDocumentReference = travelItem.contentBlocks.some(
          block => block.type === 'document_reference' && block.documentId === documentId
        );

        if (hasDocumentReference) {
          console.log('🗑️ [TravelService] handleDocumentDeleted: Removing document references from travel item', {
            travelId: travelItem.id,
            documentId
          });

          // Remove document reference blocks
          const updatedContentBlocks = removeDocumentReferenceBlocks(travelItem.contentBlocks, [documentId]);

          // Update the travel item with new content blocks
          await updateTravel(userId, tripId, travelItem.id, {
            contentBlocks: updatedContentBlocks
          });

          console.log('✅ [TravelService] handleDocumentDeleted: Successfully removed document references from travel item', {
            travelId: travelItem.id,
            documentId
          });
        }
      }
    } catch (error) {
      console.error('❌ [TravelService] handleDocumentDeleted: Error removing document references', {
        error: error.message,
        stack: error.stack
      });
    }
  }
};